window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    window.location.reload();
  }
});

class MonteKitBuilder extends HTMLElement {
  connectedCallback() {
    this.currency = this.dataset.currency || 'BRL';
    this.selections = new Map();
    this.summaryDismissed = false;
    this.kitInstanceKey = 'monte-seu-kit:instance';
    this.attributeKey = '_monte_seu_kit';
    this.perProductCap = 3;
    this.syncQueue = [];
    this.syncRunning = false;
    this.syncingFromCart = false;

    this.tierTabs = this.querySelectorAll('[data-mkb-tier-tab]');
    this.tierPanels = this.querySelectorAll('[data-mkb-tier-panel]');
    this.tabs = this.querySelectorAll('[data-mkb-tab]');
    this.bar = this.querySelector('[data-mkb-bar]');
    this.progressFillEl = this.querySelector('[data-mkb-progress-fill]');
    this.ctaEls = this.querySelectorAll('[data-mkb-cta]');

    this.activeTierIndex = 0;
    if (this.tierTabs.length) {
      this.activeQty = Number(this.tierTabs[0].dataset.qty) || 1;
      this.activePct = Number(this.tierTabs[0].dataset.pct) || 0;
    } else {
      this.activeQty = 1;
      this.activePct = 0;
    }

    this.addEventListener('click', this.handleClick.bind(this));
    document.addEventListener('change', this.handleCartQuantityChange.bind(this), { capture: true });
    document.addEventListener('cart:change', () => {
      this.syncFromCart();
    });
    this.observeCartDrawer();
    this.applyCategoryFromURL();
    this.syncFromCart();
    this.render();
  }

  observeCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    let debounceTimer = null;
    const observer = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        this.syncFromCart();
      }, 80);
    });
    observer.observe(drawer, { childList: true, subtree: true });
  }

  handleCartQuantityChange(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    const input = path.find((el) => el && el.hasAttribute && el.hasAttribute('data-line-key'));
    if (!input) return;

    const lineKey = input.getAttribute('data-line-key');
    const entry = Array.from(this.selections.entries()).find(([, item]) => item.lineKey === lineKey);
    if (!entry) return;

    if (Number(input.value) > this.perProductCap) {
      input.value = String(this.perProductCap);
      entry[1].qty = this.perProductCap;
      this.applySelectionVisuals();
      this.render();
    }
  }

  handleClick(event) {
    const tierTab = event.target.closest('[data-mkb-tier-tab]');
    if (tierTab) {
      this.switchTier(tierTab);
      return;
    }

    const tab = event.target.closest('[data-mkb-tab]');
    if (tab) {
      this.filterTab(tab);
      return;
    }

    const summaryClose = event.target.closest('[data-mkb-summary-close]');
    if (summaryClose) {
      this.summaryDismissed = true;
      this.render();
      return;
    }

    const slot = event.target.closest('[data-mkb-slot].is-filled');
    if (slot) {
      if (slot.dataset.url) {
        window.open(slot.dataset.url, '_blank', 'noopener');
      }
      return;
    }

    const minusBtn = event.target.closest('[data-mkb-minus]');
    if (minusBtn && !minusBtn.disabled) {
      this.decrementItem(minusBtn.closest('[data-mkb-card]'));
      return;
    }

    const addBtn = event.target.closest('[data-mkb-add]');
    if (addBtn && !addBtn.disabled) {
      this.incrementItem(addBtn.closest('[data-mkb-card]'));
      return;
    }

    const cta = event.target.closest('[data-mkb-cta]');
    if (cta && !cta.disabled) {
      this.submit();
    }
  }

  totalQty() {
    let sum = 0;
    this.selections.forEach((item) => {
      sum += item.qty;
    });
    return sum;
  }

  activateTier(tierTab, preserveSelections) {
    const index = Number(tierTab.dataset.mkbTierTab);

    this.tierTabs.forEach((t) => t.classList.remove('is-active'));
    tierTab.classList.add('is-active');

    this.tierPanels.forEach((panel) => {
      panel.hidden = panel.dataset.mkbTierPanel !== String(index);
    });

    this.activeTierIndex = index;
    this.activeQty = Number(tierTab.dataset.qty) || 1;
    this.activePct = Number(tierTab.dataset.pct) || 0;
    this.summaryDismissed = false;

    let toRemove = [];
    if (!preserveSelections) {
      toRemove = Array.from(this.selections.values()).filter((item) => item.lineKey);
      this.selections.clear();
    }

    this.querySelectorAll('[data-mkb-card].is-selected').forEach((card) => {
      card.classList.remove('is-selected');
    });

    if (preserveSelections) {
      this.applySelectionVisuals();
      const activeCategoryTab = Array.from(this.tabs).find((t) => t.classList.contains('is-active'));
      if (activeCategoryTab) this.reapplyCategoryFilter(activeCategoryTab);
    } else {
      const allTab = this.querySelector('[data-mkb-tab="all"]') || this.tabs[0];
      if (allTab) this.filterTab(allTab);
    }

    this.render();

    if (toRemove.length > 0) {
      this.removeLinesFromCart(toRemove.map((item) => item.lineKey));
    }
  }

  switchTier(tierTab) {
    this.activateTier(tierTab, true);
  }

  sortedTierTabs() {
    return Array.from(this.tierTabs).sort((a, b) => Number(a.dataset.qty) - Number(b.dataset.qty));
  }

  tryUpgradeTier() {
    const tabsSorted = this.sortedTierTabs();
    const currentPos = tabsSorted.findIndex(
      (t) => Number(t.dataset.mkbTierTab) === this.activeTierIndex
    );
    const nextTab = tabsSorted[currentPos + 1];
    if (!nextTab) return false;
    this.activateTier(nextTab, true);
    return true;
  }

  maybeSyncTierDown() {
    const total = this.totalQty();
    if (total >= this.activeQty) return false;

    const target = this.sortedTierTabs().find(
      (t) => Number(t.dataset.qty) === total && Number(t.dataset.qty) < this.activeQty
    );

    if (!target) return false;
    this.activateTier(target, true);
    return true;
  }

  reapplyCategoryFilter(tab) {
    const category = tab.dataset.mkbTab;
    const activePanel = this.tierPanels[this.activeTierIndex];
    if (!activePanel) return;
    activePanel.querySelectorAll('[data-mkb-card]').forEach((card) => {
      const cats = (card.dataset.categories || '').split(',');
      const show = category === 'all' || cats.includes(category);
      card.hidden = !show;
    });
  }

  filterTab(tab) {
    this.tabs.forEach((t) => t.classList.remove('is-active'));
    tab.classList.add('is-active');
    this.reapplyCategoryFilter(tab);
    this.updateCategoryURL(tab.dataset.mkbTab);
  }

  applyCategoryFromURL() {
    if (!this.tabs || !this.tabs.length) return;
    let categoria;
    try {
      categoria = new URLSearchParams(window.location.search).get('categoria');
    } catch (error) {
      return;
    }
    if (!categoria) return;
    const target = Array.from(this.tabs).find((t) => t.dataset.mkbTab === categoria);
    if (target) this.filterTab(target);
  }

  updateCategoryURL(categoria) {
    try {
      const url = new URL(window.location.href);
      if (categoria && categoria !== 'all') {
        url.searchParams.set('categoria', categoria);
      } else {
        url.searchParams.delete('categoria');
      }
      window.history.replaceState(window.history.state, '', url);
    } catch (error) {
      // URL update is a nice-to-have; never block the UI on it.
    }
  }

  applySelectionVisuals() {
    const activePanel = this.tierPanels[this.activeTierIndex];
    if (!activePanel) return;

    activePanel.querySelectorAll('[data-mkb-card]').forEach((card) => {
      const id = card.dataset.variantId;
      const item = this.selections.get(id);
      const qty = item ? item.qty : 0;

      card.classList.toggle('is-selected', qty > 0);

      const addBtn = card.querySelector('[data-mkb-add]');
      const minusBtn = card.querySelector('[data-mkb-minus]');
      const qtyEl = card.querySelector('[data-mkb-qty]');

      if (qtyEl) {
        qtyEl.hidden = qty <= 0;
        if (qty > 0) qtyEl.textContent = String(qty);
      }

      if (minusBtn) {
        minusBtn.disabled = qty <= 0;
      }

      if (addBtn) {
        addBtn.disabled = qty >= this.perProductCap;
        addBtn.setAttribute('aria-pressed', qty > 0 ? 'true' : 'false');
      }
    });
  }

  getKitInstance() {
    if (!this.kitInstance) {
      try {
        this.kitInstance = localStorage.getItem(this.kitInstanceKey);
      } catch (error) {
      }
      if (!this.kitInstance) {
        this.kitInstance = `monte-seu-kit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        try {
          localStorage.setItem(this.kitInstanceKey, this.kitInstance);
        } catch (error) {
        }
      }
    }
    return this.kitInstance;
  }

  prepareBundledSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  }

  dispatchCartChange(cart) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } })
    );
  }

  async addLineToCart(variantId, quantity) {
    const sections = this.prepareBundledSections();
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity, properties: { [this.attributeKey]: this.getKitInstance() } }],
        sections,
      }),
    });

    if (!response.ok) {
      throw new Error(`Cart add failed: ${response.status}`);
    }

    const cart = await response.json();
    const line = (cart.items || []).find(
      (item) =>
        String(item.id) === String(variantId) &&
        item.properties &&
        item.properties[this.attributeKey] === this.kitInstance
    );
    return { cart, lineKey: line ? line.key : null };
  }

  async changeLineQuantity(lineKey, quantity) {
    const sections = this.prepareBundledSections();
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity, sections }),
    });

    if (!response.ok) {
      throw new Error(`Cart change failed: ${response.status}`);
    }

    return response.json();
  }

  async removeLinesFromCart(lineKeys) {
    try {
      const sections = this.prepareBundledSections();
      const updates = {};
      lineKeys.forEach((key) => {
        updates[key] = 0;
      });

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates, sections }),
      });

      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.status}`);
      }

      const cart = await response.json();
      this.dispatchCartChange(cart);
    } catch (error) {
      console.error('[monte-seu-kit]', error);
    }
  }

  incrementItem(card) {
    const id = card.dataset.variantId;
    const existing = this.selections.get(id);
    const currentQty = existing ? existing.qty : 0;

    if (currentQty >= this.perProductCap) return;

    if (this.totalQty() >= this.activeQty) {
      this.tryUpgradeTier();
    }

    if (existing) {
      existing.qty += 1;
    } else {
      this.selections.set(id, {
        variantId: id,
        price: Number(card.dataset.price),
        title: card.dataset.title,
        img: card.dataset.img,
        url: card.dataset.url,
        qty: 1,
        lineKey: null,
      });
      this.summaryDismissed = false;
    }

    this.applySelectionVisuals();
    this.render();
    this.scheduleSync(id);
  }

  decrementItem(card) {
    const id = card.dataset.variantId;
    const existing = this.selections.get(id);
    if (!existing || existing.qty <= 0) return;

    existing.qty -= 1;

    const tierChanged = this.maybeSyncTierDown();
    if (!tierChanged) {
      this.applySelectionVisuals();
      this.render();
    }

    this.scheduleSync(id);
  }

  scheduleSync(id) {
    if (!this.syncQueue.includes(id)) this.syncQueue.push(id);
    clearTimeout(this.syncTimer);
    this.syncTimer = setTimeout(() => {
      this.runSyncQueue();
    }, 280);
  }

  async runSyncQueue() {
    if (this.syncRunning) return;
    this.syncRunning = true;
    try {
      while (this.syncQueue && this.syncQueue.length > 0) {
        const id = this.syncQueue.shift();
        await this.syncItem(id);
      }
    } finally {
      this.syncRunning = false;
    }
  }

  async syncItem(id) {
    const item = this.selections.get(id);
    if (!item) return;

    const targetQty = item.qty;

    try {
      let cart;
      if (!item.lineKey) {
        if (targetQty <= 0) {
          this.selections.delete(id);
          return;
        }
        const result = await this.addLineToCart(id, targetQty);
        item.lineKey = result.lineKey;
        cart = result.cart;
      } else {
        cart = await this.changeLineQuantity(item.lineKey, targetQty);
        if (targetQty <= 0) {
          this.selections.delete(id);
        }
      }

      this.dispatchCartChange(cart);
    } catch (error) {
      console.error('[monte-seu-kit]', error);
    }
  }

  fillTemplate(template, vars) {
    return template.replace(/\[(\w+)\]/g, (match, key) => (key in vars ? vars[key] : match));
  }

  renderSlots(activePanel) {
    const slotsContainer = activePanel.querySelector('[data-mkb-slots]');
    if (!slotsContainer) return;

    const slots = Array.from(slotsContainer.querySelectorAll('[data-mkb-slot]'));
    const items = Array.from(this.selections.values()).filter((item) => item.qty > 0);
    const total = this.totalQty();
    const emptyCount = Math.max(0, this.activeQty - total);
    const neededCount = items.length + emptyCount;

    while (slots.length < neededCount) {
      const slot = document.createElement('div');
      slot.className = 'mkb__slot';
      slot.setAttribute('data-mkb-slot', '');
      slot.setAttribute('aria-label', 'Vaga vazia');
      slotsContainer.appendChild(slot);
      slots.push(slot);
    }

    slots.forEach((slot, index) => {
      if (index >= neededCount) {
        slot.hidden = true;
        slot.classList.remove('is-filled');
        delete slot.dataset.variantId;
        delete slot.dataset.url;
        slot.innerHTML = '';
        return;
      }

      slot.hidden = false;
      const item = items[index];
      if (item) {
        slot.classList.add('is-filled');
        slot.dataset.variantId = item.variantId;
        slot.dataset.url = item.url || '';
        slot.setAttribute('aria-label', `Ver ${item.title}`);
        const img = item.img ? `<img src="${item.img}" alt="${item.title}">` : '';
        slot.innerHTML = `${img}<span class="mkb__slot-qty">${item.qty}</span>`;
      } else {
        slot.classList.remove('is-filled');
        delete slot.dataset.variantId;
        delete slot.dataset.url;
        slot.setAttribute('aria-label', 'Vaga vazia');
        slot.innerHTML = '';
      }
    });
  }

  render() {
    const total = this.totalQty();
    let subtotal = 0;
    this.selections.forEach((item) => {
      subtotal += item.price * item.qty;
    });

    const complete = this.activeQty > 0 && total >= this.activeQty;
    const pct = complete ? this.activePct : 0;
    const discount = Math.round(subtotal * (pct / 100));
    const totalPrice = subtotal - discount;

    this.bar.classList.toggle('is-visible', total > 0);
    this.ctaEls.forEach((btn) => {
      btn.disabled = !complete;
    });
    this.progressFillEl.style.width = `${Math.min(100, (total / this.activeQty) * 100)}%`;

    const activePanel = this.tierPanels[this.activeTierIndex];
    if (!activePanel) return;

    const summaryEl = activePanel.querySelector('[data-mkb-summary]');
    if (summaryEl) summaryEl.classList.toggle('is-visible', total > 0 && !this.summaryDismissed);

    this.renderSlots(activePanel);

    const messageEl = activePanel.querySelector('[data-mkb-message]');
    if (messageEl) {
      const vars = {
        qty: this.activeQty,
        pct: this.activePct,
        remaining: Math.max(0, this.activeQty - total),
        count: total,
      };
      let template;
      if (total === 0) {
        template = messageEl.dataset.empty;
      } else if (!complete) {
        template = messageEl.dataset.incomplete;
      } else {
        template = messageEl.dataset.complete;
      }
      messageEl.textContent = this.fillTemplate(template, vars);
    }

    const totalsEl = activePanel.querySelector('[data-mkb-totals]');
    if (totalsEl) {
      totalsEl.hidden = total === 0;
      const subtotalEl = activePanel.querySelector('[data-mkb-subtotal]');
      const discPctEl = activePanel.querySelector('[data-mkb-disc-pct]');
      const discountEl = activePanel.querySelector('[data-mkb-discount]');
      const totalEl = activePanel.querySelector('[data-mkb-total]');
      if (subtotalEl) subtotalEl.textContent = this.formatMoney(subtotal);
      if (discPctEl) discPctEl.textContent = `${pct}%`;
      if (discountEl) discountEl.textContent = `-${this.formatMoney(discount)}`;
      if (totalEl) totalEl.textContent = this.formatMoney(totalPrice);
    }
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: this.currency });
  }

  async syncFromCart() {
    if (this.syncingFromCart) return;
    this.syncingFromCart = true;
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return;
      let cart = await response.json();
      const kitInstance = this.getKitInstance();

      let kitLines = (cart.items || []).filter(
        (line) => line.properties && line.properties[this.attributeKey] === kitInstance
      );

      const overCapLines = kitLines.filter((line) => line.quantity > this.perProductCap);
      if (overCapLines.length > 0) {
        for (const line of overCapLines) {
          cart = await this.changeLineQuantity(line.key, this.perProductCap);
        }
        kitLines = (cart.items || []).filter(
          (line) => line.properties && line.properties[this.attributeKey] === kitInstance
        );
        this.dispatchCartChange(cart);
      }

      const total = kitLines.reduce((sum, line) => sum + line.quantity, 0);

      if (total > 0) {
        const tabsSorted = this.sortedTierTabs();
        const targetTab = tabsSorted.find((t) => Number(t.dataset.qty) >= total) || tabsSorted[tabsSorted.length - 1];
        if (targetTab) {
          this.tierTabs.forEach((t) => t.classList.remove('is-active'));
          targetTab.classList.add('is-active');
          const index = Number(targetTab.dataset.mkbTierTab);
          this.tierPanels.forEach((panel) => {
            panel.hidden = panel.dataset.mkbTierPanel !== String(index);
          });
          this.activeTierIndex = index;
          this.activeQty = Number(targetTab.dataset.qty) || 1;
          this.activePct = Number(targetTab.dataset.pct) || 0;
        }
      }

      const pending = new Map();
      this.selections.forEach((item, id) => {
        if (!item.lineKey) pending.set(id, item);
      });
      this.selections = pending;

      kitLines.forEach((line) => {
        const variantId = String(line.id);
        if (this.selections.has(variantId)) return;
        const card = this.querySelector(`[data-mkb-card][data-variant-id="${CSS.escape(variantId)}"]`);
        if (!card) return;
        this.selections.set(variantId, {
          variantId,
          price: Number(card.dataset.price),
          title: card.dataset.title,
          img: card.dataset.img,
          url: card.dataset.url,
          qty: line.quantity,
          lineKey: line.key,
        });
      });

      this.applySelectionVisuals();
      this.render();
      this.applyCartDrawerCaps();
    } catch (error) {
      console.error('[monte-seu-kit]', error);
    } finally {
      this.syncingFromCart = false;
    }
  }

  applyCartDrawerCaps() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    this.selections.forEach((item) => {
      if (!item.lineKey) return;
      const input = drawer.querySelector(`[data-line-key="${CSS.escape(item.lineKey)}"]`);
      if (!input) return;

      input.max = String(this.perProductCap);
      const addBtn = input.closest('quantity-selector')?.querySelector('button:last-of-type');
      if (addBtn) addBtn.disabled = item.qty >= this.perProductCap;
    });
  }

  async submit() {
    window.location.href = '/cart';
  }
}

customElements.define('monte-kit-builder', MonteKitBuilder);
